import React from "react";
import styled from "styled-components";
import Head from "next/head";
import ConditionalNavWrapper from "components/ConditionalNavWrapper";

const PageWrapper = styled.div`
  max-width: 1024px;
  margin: 35px 25px 40px 25px;
  padding: 0;

  ${({ theme }) => theme.mq.lg`
    margin: 38px auto 70px auto;
    padding: 0 50px;
  `};
`;

const Title = styled.div`
  font-size: 32px;
  margin-bottom: 20px;
  line-height: 32px;

  ${({ theme }) => theme.mq.lg`
    font-size: 64px;
    line-height: 77px;
    margin-bottom: 35px;
  `};
`;

const Subtitle = styled.div`
  font-size: 24px;
  margin: 40px 0 12px 0;
`;

const IndentSubtitle = styled.div`
  font-size: 24px;
  margin: 40px 0 12px 50px;
`;

const Body = styled.div`
  font-size: 16px;
  text-align: justify;

  a {
    color: ${({ theme }) => theme.themeColors.primary};
  }
`;

const Block = styled.div`
  text-align: justify;
  margin: 0 20px;

  ${({ theme }) => theme.mq.lg`
    margin: 0 100px;
  `};
`;

export default function ProviderAgreement() {
  return (
    <ConditionalNavWrapper>
      <PageWrapper>
        <Head>
          <title>GoodFriend | Provider Agreement</title>
        </Head>
        <Title>Provider Agreement</Title>
        <Body>
          This GoodFriend Provider Agreement (“Agreement”) contains the terms
          and conditions that apply to the use by you and the entity that you
          represent (“Provider” or “You”) of the Services offered by Kinship
          Partners Inc. d/b/a GoodFriend (“GoodFriend”), including but not
          limited to{" "}
          <a
            href="https://www.goodfriend.com/"
            target="_blank"
            rel="noreferrer"
          >
            www.goodfriend.com
          </a>{" "}
          and all affiliated websites and applications owned and operated by
          GoodFriend (the “GoodFriend Website”). For the purposes of this
          Agreement, “Provider” shall mean both You, an individual, and the
          entity on behalf of which You engaged GoodFriend to provide the
          Services (as defined below), and shall include all of Your
          professionals, employees, Providers, and staff members. This Agreement
          hereby incorporates by reference any Provider account page accessible
          by Provider through the Services (the “Account Page”) or other
          statement of work or order form entered into by the parties and any
          addenda thereto.
          <br /> <br />
          BY AGREEING TO THESE TERMS, YOU REPRESENT AND WARRANT THAT YOU ARE
          DULY AUTHORIZED TO ENTER INTO THIS AGREEMENT AND ACKNOWLEDGE AND AGREE
          THAT ALL USE OF THE SERVICES (AS DEFINED BELOW) BY PROVIDER IS SUBJECT
          TO THESE TERMS AND CONDITIONS. IF YOU DO NOT AGREE TO THESE TERMS OR
          ARE NOT AUTHORIZED TO ENTER INTO THIS AGREEMENT AND BIND THE ENTITY
          YOU REPRESENT TO THESE TERMS AND CONDITIONS, THEN DO NOT ENTER THIS
          AGREEMENT.
        </Body>
        <Subtitle>1. Services.</Subtitle>
        <Block>
          1. Services. Subject to the terms and conditions of this Agreement and
          payment of the fees set forth herein, GoodFriend will provide a
          variety of services via the GoodFriend Website (“Services”), which may
          include, but are not limited to: (i) hosting Provider profile web
          pages, and marketing Providers and their services on the Website; (ii)
          provide scheduling services; and (iii) billing, collection, and
          payment services (“Payment Services”).
          <br /> <br />
          2. Provider Access. Subject to the terms and conditions of this
          Agreement, GoodFriend grants Provider the right to access and use the
          Services and the GoodFriend Website and to permit its employees,
          agents, and subcontractors the right to access and use the Services
          and the GoodFriend Website solely on Provider’s behalf; provided,
          however, that You shall be responsible for any use of the Services and
          the GoodFriend Website by such employees, agents, and subcontractors,
          and any other person or entity who gains access to the Services
          through Your Account credentials. During the Term and subject to the
          terms and conditions of this Agreement, You grant GoodFriend the right
          to access and use Provider’s management system, calendar system, and
          patient database (“Provider’s Systems”), and to copy, extract, and use
          all data necessary to provide the Services.
          <br /> <br />
          3. Provider Information. You acknowledge and agree that to provide the
          Services, GoodFriend shall make information about Provider and the
          products and services offered and sold by Provider (collectively,
          “Provider Services”) available to end users of the GoodFriend Website
          (“End Users”), including End Users that book or purchase a Provider
          Service through GoodFriend (“Customers”). Such information includes,
          without limitation: (i) Provider’s address, phone number, fax number,
          and email address; (ii) information regarding Provider appointment
          availability and cancellation, rescheduling, and refund policies;
          (iii) general information about Provider’s company, products, and
          services; and (iv) trademarks, logos, and such other domains, images,
          and materials that Provider provides to GoodFriend (“Provider Logos”)
          (collectively, “Provider Information”). GoodFriend and its partners
          may use, modify, display, and reproduce Provider Information in
          connection with providing and marketing the Services and the
          GoodFriend Website. You may request that GoodFriend modify Provider
          Information with additional information, links, or services.
          GoodFriend may incorporate such modifications and additional
          information into Provider’s profile on the GoodFriend Website in
          GoodFriend’s sole discretion.
          <br /> <br />
          4. Accounts. Each Provider shall create and maintain an account and
          password (“Account”) to use the Services and GoodFriend Website,
          including uploading, reviewing, and updating Provider Information.
          Account information shall be held in strict confidence by Provider and
          Provider shall be solely responsible for any disclosures of its
          Account information or use thereof by any unauthorized party. Provider
          shall immediately notify GoodFriend of any unauthorized use, access,
          or other compromise of an Account (including any password); or if an
          authorized Provider contact is no longer employed or authorized, or
          otherwise ceases to be part of any Provider. GoodFriend reserves the
          right to disable any Account upon its reasonable belief that the
          security with respect to that Account has been or likely will be
          compromised.
          <br /> <br />
          5. Reviews. GoodFriend may solicit feedback from Customers and End
          Users regarding Providers and Provider Services (“Reviews”), and
          GoodFriend may, in its sole discretion, make these Reviews available
          on the GoodFriend Website and in other media. GoodFriend reserves the
          right, but does not undertake the obligation, to remove Reviews.
          However, GoodFriend shall have no obligation to (i) review Reviews for
          veracity, accuracy, or content, or (ii) remove any Reviews. Further,
          GoodFriend shall have no liability to Provider with respect to, and
          Provider hereby releases GoodFriend from, all claims related to,
          arising from or in connection with Reviews, including without
          limitation claims that a Review is defamatory, libelous, false,
          misleading, unfair, deceptive, or disparaging. Provider represents and
          warrants that it will not attempt to improperly influence or
          fraudulently create Reviews of its or other Providers. GoodFriend may
          also display through the Services certain reviews of Providers
          submitted directly to Provider or an agent of Provider. Provider
          agrees and acknowledges that GoodFriend may, in its sole discretion,
          remove or refrain from publishing any such Review including, but not
          limited to, if it violates GoodFriend’s policies, or applicable laws,
          rules, or regulations.
          <br /> <br />
          6. Content. GoodFriend shall develop, compile, modify, or otherwise
          maintain all content on the GoodFriend Website or as part of the
          Services, including any Provider photographs, and any content provided
          by Provider or Customers. Provider acknowledges and agrees that
          GoodFriend may make content from the GoodFriend Website, including
          Provider Information, available on third-party websites, platforms,
          and through marketing initiatives.
        </Block>
        <Subtitle>2. Software</Subtitle>
        <Body>
          a. Acceptance. You may choose to review, download, or otherwise use
          certain software applications or other code and functionality from the
          GoodFriend Website or otherwise provided by GoodFriend (“Software”).
          As a condition to using the Software, You must review and agree to
          this Agreement, the{" "}
          <a href="/terms-service">GoodFriend Terms and Conditions</a>, and any
          other terms and conditions that may apply (collectively, the “Software
          Terms”). If there is a conflict between this Agreement and the
          GoodFriend Terms and Conditions, this Agreement will control. If You
          use the Software, You shall be deemed to have accepted the Software
          Terms.
          <br /> <br />
          b. Software. Subject to the terms and conditions of this Agreement and
          during the Term, GoodFriend grants Provider a limited, non-exclusive,
          non-sublicensable, non-transferable and revocable right and license to
          use the Software solely for Provider’s internal business purposes and
          solely in connection with the relevant Provider Services. GoodFriend
          reserves all rights to the Software not expressly granted hereunder.
          Subject to the terms and conditions of this Agreement and during the
          Term hereof, GoodFriend hereby grants to You a limited, non-exclusive,
          non-sublicensable, non-transferable, and revocable license to use the
          Software on Provider’s authorized website (“Provider Website”) solely
          for the intended purposes to access and display certain GoodFriend
          Content (as defined below) to visitors of the Provider Website. Upon
          GoodFriend’s request, You shall immediately remove any Software from
          the Provider Website. Provider is not licensed to use the Software for
          any other purpose and nothing in this Agreement shall be deemed to
          grant You any other right, title, or interest in the Software
          <br /> <br />
          c. GoodFriend Marks. Subject to the terms and conditions of this
          Agreement, Provider may display GoodFriend’s name and any trade name,
          trademark, service mark, or logo (“GoodFriend Marks”) on the Provider
          Website in connection with the presentation of the Software and
          direction to the GoodFriend website. All goodwill related to such name
          and logo and all uses thereof, and any accrued goodwill, shall inure
          solely to the benefit of GoodFriend. Provider shall immediately cease
          such use or modify its use of the GoodFriend Marks upon GoodFriend’s
          written request. Upon any termination of this Agreement, Provider
          shall immediately cease all use of GoodFriend Marks.
          <br /> <br />
          d. Provider Website Terms. If the Provider Website includes any data,
          information, or content provided or otherwise made available by or on
          behalf of GoodFriend through or in connection with the Services or the
          GoodFriend Website, GoodFriend Marks, links and search functionality
          leading to the GoodFriend Website, or data and html and other code
          that accompanies the Software (“GoodFriend Content”), Provider shall
          require that Provider Website visitors affirmatively accept terms and
          conditions in connection with such use of the Provider Website. Such
          terms must include all legally required and otherwise appropriate
          disclaimers related to the applicable GoodFriend Content, and
          provisions regarding Provider’s collection and processing of website
          visitors’ personal information in compliance with all applicable laws
          and regulations and consistent with this Agreement.
          <br /> <br />
          e. Restrictions. Except as expressly authorized under this Agreement,
          Provider may not (i) copy, rent, lease, sell, lease, license,
          transfer, assign, sublicense, redistribute, disassemble, aggregate,
          index, reverse engineer or decompile, derive source code or algorithms
          from, modify or alter, interfere with, defeat, avoid, disrupt, bypass,
          remove, disrupt or disable any part of the GoodFriend Website; (ii)
          circumvent or attempt to circumvent any restrictions on, access to, or
          use of the GoodFriend Website, or any of their constituent components;
          (iii) introduce any virus, worm, trap door, back door, timer, time
          bomb, authorization codes, or other device that would access, modify,
          interfere with or disrupt the use of the GoodFriend Website; (iv) use
          the GoodFriend Website for any unlawful purpose, including to phish,
          spam, or distribute malware; (v) otherwise use the GoodFriend Website
          or Services on behalf of any third party or on any websites other than
          the Provider Website and the Provider Services expressly articulated
          by Provider to GoodFriend, or to create or maintain a separate online
          scheduling platform, or other similar product or service; (vi) resell,
          disclose, publish, or distribute the GoodFriend Website, including any
          information created, received, processed, or provided through the
          Services or the GoodFriend Website; (vii) use the GoodFriend Website
          in any manner or for any purpose that violates any law or regulation,
          or any right of any person, including, but not limited to,
          intellectual property rights, rights of privacy, or rights of
          publicity; (viii) cache or store any content obtained via use of the
          GoodFriend Website; or (ix) remove or alter any branding, identifying,
          or notices included with the Software including any GoodFriend Marks.
          Further, Provider shall not place any Software or GoodFriend Content
          on Provider Websites that include content that is offensive, abusive,
          harassing, threatening, discriminatory, vulgar, pornographic, or
          otherwise inappropriate, as determined by GoodFriend in its sole
          discretion. Provider may not display the GoodFriend Content or
          Software in a manner that does not permit successful linking or
          redirection to the GoodFriend Website.
        </Body>
        <IndentSubtitle>Provider Responsibilities.</IndentSubtitle>
        <Body>
          a. Provider Information. Provider shall cooperate with GoodFriend, its
          designated agents, or independent contractors to provide and verify
          information that GoodFriend in its sole discretion deems necessary to
          confirm Provider’s professional qualifications, legitimacy, and
          veracity. Provider will promptly update and notify GoodFriend of any
          change in Provider Information. Provider is solely responsible for any
          liability or expense resulting from outdated or inaccurate Provider
          Information. Provider further agrees that GoodFriend may, in its sole
          discretion, modify the processes and criteria it uses to evaluate
          Provider qualifications, including with respect to any specialty
          listing, and may reject a Provider from being included or listed in
          the Services for failure to meet GoodFriend’s eligibility standards,
          in GoodFriend’s sole discretion.
          <br /> <br />
          b. Provider Services; Acceptance of Customers. Provider’s acceptance
          of an appointment scheduling request from GoodFriend for a Customer
          constitutes, for the purposes of this Agreement, Provider’s acceptance
          of such Customer as a customer of Provider. Provider is solely
          responsible for all products sold and services rendered to Customer.
          Except for GoodFriend’s Payment Services obligations, GoodFriend shall
          have no responsibility or liability whatsoever for Customer’s payment
          for Provider Services. Provider expressly acknowledges and agrees that
          GoodFriend shall not be responsible or liable for the provision or
          fulfilment of Provider Services.
          <br /> <br />
          c. Scheduling Requests. After a Customer submits a scheduling request
          via the GoodFriend Website, Provider must respond to Customer
          scheduling request within a reasonable amount of time, but in no event
          more than twenty-four (24) hours after receiving GoodFriend’s request
          for an appointment on behalf of a Customer. Such response must include
          an appointment confirmation or rejection. Provider acknowledges and
          agrees that repeated rescheduling, rejection, or cancelling of
          Customer appointments may result in Provider being removed from, or
          otherwise affected in the order of display of, appointment listings
          available to End Users, in GoodFriend’s sole discretion.
          <br /> <br />
          d. Customer Personal Information. If a Customer submits personal
          information (“Customer Personal Information”) via the GoodFriend
          Website, Provider acknowledges, agrees, releases, and indemnifies
          GoodFriend from all claims and liability arising out of, related to,
          or in connection with Provider’s access to, use of, or disclosure of
          Customer Personal Information. Without limiting the foregoing,
          Provider has no rights in the Customer Personal Information maintained
          by GoodFriend other than as expressly provided hereunder. If
          GoodFriend suspects or becomes aware that a Provider is using any
          Customer Personal Information obtained through the Services or the
          GoodFriend Website in a manner that is inconsistent with the terms of
          this Agreement, GoodFriend’s terms of use or privacy policy, or
          applicable law, GoodFriend may, without limiting any of its other
          remedies available at law or in equity, request that Provider
          immediately cease such inconsistent use and immediately suspend or
          terminate this Agreement or such Provider’s rights with respect to the
          Services and the GoodFriend Website.
          <br /> <br />
          e. Contact. Provider agrees that GoodFriend may contact You or any
          agent, employee, or member of Provider regarding the Services, the
          Software, or the GoodFriend Website. If Provider previously opted out
          of receiving any email, facsimile, or telephone communications
          (including communications made using an automated dialing system or
          prerecorded voice) from or on behalf of GoodFriend, then Provider
          expressly opts back in to receiving such email and facsimile
          communications and hereby withdraws its opt-out.
        </Body>
        <IndentSubtitle>Fees and Payments</IndentSubtitle>
        <Body>
          a. Services Fees. Provider shall pay GoodFriend any fees specified in
          the applicable insertion order or order form, or as otherwise
          specified on Provider’s Account Page. Prices will not include any
          taxes that may apply, and Provider agrees to pay any sales and other
          taxes that may apply (excluding GoodFriend’s income taxes). Any and
          all late payment amounts shall bear interest at the rate of the lesser
          of one and one half percent (1.5%) per month or the maximum amount
          permissible by law. Except as otherwise may be agreed between the
          parties, GoodFriend reserves the right to change from time to time the
          amount or structure of the fees for any of the Services (including the
          Payment Services).
          <br /> <br />
          b. Payment Services. GoodFriend shall provide the Payment Services
          through which Customers can book Provider Services and pay GoodFriend
          for such Provider Services. Provider shall specify the price for each
          Provider Service in its Provider Account. Any changes to prices for
          Provider Services will be published within five (5) business days, and
          Provider expressly agrees to accept appointments made under previous
          pricing if Provider does not provide GoodFriend adequate prior notice
          of any price changes. Subject to the terms and conditions herein,
          GoodFriend shall pay Provider the amount specified in the Provider
          Account Page (“Provider Payments”) after a Customer completes a
          purchase of the corresponding Provider Service and Provider completes
          and fulfills such Provider Service. Provider Payments shall be issued
          to Provider via the ACH or to the account connected by Provider
          through GoodFriend’s payment processor. If a Customer requests a
          cancellation or refund or initiates a chargeback for Provider
          Services, GoodFriend shall deduct the corresponding amount, including
          a transaction and chargeback fee if imposed on GoodFriend, from
          Provider Payments for such cancellations, refunds, or chargebacks.
          Provider Payments shall be issued within 30 days after the date that
          the Provider Services were provided to Customer. Without limiting the
          foregoing, GoodFriend reserves the right to extend the period in which
          it issues refunds to Customers, in which case Provider shall not
          receive Provider Payments until such refunds are processed by
          GoodFriend. GoodFriend expressly reserves the right to withhold
          Provider Payments in whole or in part if it suspects that Provider has
          violated this Agreement, or any applicable law, rule, or regulation
          (including rules established by payment processor, banks, and card
          brands), or if GoodFriend suspects or determines that Provider or any
          third party under Provider’s reasonable control engaged in fraud, as
          determined by GoodFriend in its sole discretion. In the event of a
          dispute regarding amounts due, Provider agrees that GoodFriend’s
          calculation of amounts shall control.
          <br /> <br />
          c. Cancellation/Refunds. Provider shall establish cancellation,
          rescheduling, and refund policies for Customers and inform GoodFriend
          in writing of such policies through the Provider Account. Provider
          Payments shall be issued in accordance with valid Customers for whom
          it fulfills and completes Provider Services, and Provider shall not be
          entitled to any Provider Payments resulting from a scheduling request
          that is subsequently cancelled or rescheduled, or refunded, in
          accordance with GoodFriend’s policies. Provider acknowledges and
          agrees that GoodFriend’s cancellation, rescheduling, and refund
          policies shall be established and administered by GoodFriend at
          GoodFriend’s sole discretion, and GoodFriend reserves the right to
          extend or modify such policies if it believes, in its sole discretion,
          that such extension or modification is appropriate or necessary for
          customer service, risk reduction, or compliance purposes. If Provider
          or Provider’s personnel receives a cancellation or refund request,
          Provider shall contact GoodFriend and not provide a refund directly or
          take any other responsive action, unless GoodFriend provides written
          instructions to the contrary. GoodFriend may issue refunds to users as
          required by law or in its discretion. Notwithstanding any language to
          the contrary in the Agreement, if GoodFriend issues (or notifies
          Provider that it is planning to issue) a full or partial refund to a
          Customer, GoodFriend shall have the right to be reimbursed for the
          applicable amount by Provider, or to withhold or deduct the applicable
          amount from Provider Payments (including future Provider Payments) in
          GoodFriend’s discretion; and if GoodFriend requests that Provider
          issue a refund to a Customer directly instead of reimbursing
          GoodFriend, Provider shall follow GoodFriend’s instructions.
          Provider’s obligations under this section shall apply in all events,
          including if Provider does not or cannot provide the applicable
          Provider Service.
          <br /> <br />
          d. Penalties. Provider is responsible for any penalties or fines
          imposed in relation to Provider Services by GoodFriend’s payment
          processor, acquirer, or card brands resulting from Provider’s use of
          Payment Processing Services in a manner not permitted by this
          Agreement, rules and regulations imposed by GoodFriend’s payment
          processor or acquirer, any other law, rule, or regulation, or
          resulting from fraud by Provider or a party under Provider’s
          reasonable control.
          <br /> <br />
          e. Additional Payment Terms. By entering this Agreement, You are
          expressly consenting to the Stripe Connected Account Agreement
          (https://stripe.com/connect-account/legal), which is incorporated
          herein by reference.
        </Body>
        <IndentSubtitle>
          Representations, Warranties, and Covenants.
        </IndentSubtitle>
        <Body>
          a. Provider Representations and Warranties.
          <br /> <br />
          {"   "}i. You represent and warrant that You have the right, power,
          and authority to enter into this Agreement and bind the Provider
          entity that You represent and on whose behalf You enter this
          Agreement. Further, You represent, warrant, and covenant that at all
          times during the Term: (i) Provider shall provide GoodFriend with
          complete and accurate information about Provider Services, expertise,
          and specialties (in accordance with and subject to GoodFriend’s
          then-current guidelines), association memberships, contact information
          (including Provider professional address and phone and fax numbers),
          and business / customer policies; (ii) Provider shall provide
          GoodFriend accurate and up-to-date information regarding Provider’s
          appointment availability; (iii) Provider shall comply with all
          applicable laws, rules, and regulations; (iv) Provider possesses the
          requisite valid, unexpired, unrevoked, and unrestricted licenses,
          authorizations, and certifications as required by law, rule, or
          regulation (and shall provide proof of the same to GoodFriend upon
          GoodFriend’s written request); (v) Provider and Provider’s employees,
          contractors, and agents fulfilling the Provider Services have had
          proper education and training relating to all Provider Services; (vi)
          Provider treats all Customers without regard to such Customers’ race,
          religion, ethnicity, gender, national origin, sexual orientation,
          disability, or any other unlawful considerations; (vii) the Provider
          primarily providing the Provider Services for an appointment shall be
          the person or entity with whom such appointment is booked via the
          GoodFriend Website and Services; (viii) Provider shall use, and ensure
          that all of its employees, officers, contractors, and agents use, the
          Services and the GoodFriend Website in compliance with all applicable
          laws and regulations, including, without limitation, competition,
          advertising, marketing, and consumer protection laws and regulations;
          (ix) Provider has the right to enter into and subject its corporate
          entity, employees, and agents to the terms and conditions of this
          Agreement; (x) Provider’s business information and financial account
          information provided to GoodFriend pursuant to this Agreement are
          accurate and that the undersigned is the authorized person to receive
          the funds sent by GoodFriend; and (xi) Provider possesses all right,
          title, and interest necessary for GoodFriend to provide the Services
          to Provider. Provider further represents and warrants that it will
          notify GoodFriend within five (5) days with any additional information
          requested by GoodFriend, any changes with respect to any Provider
          Information, or if at any point Provider is no longer in compliance
          with any of the representations and warranties in this Agreement.
          <br /> <br />
          {"   "}ii. Competition. Provider represents and warrants that it will
          not, and will ensure that each of its employees, agents, independent
          contractors, and any other person or entity gaining access to the
          Services via Provider’s Account credentials will not, engage or
          participate in any act or omission involving the use of the Services:
          (i) to establish, attempt to establish, or enforce, directly or
          indirectly, any agreement or coordination of (A) the prices charged
          for any product or service; (B) the kinds, frequencies, or amounts of
          any product or service offered; or (C) the customer or customer
          categories for any product or service, or otherwise engage or attempt
          to engage in price fixing, output restriction, or customer or market
          allocation; or (ii) to engage in any anti-competitive, deceptive, or
          unfair act, omission, or practice, or to otherwise violate applicable
          antitrust, competition, or consumer protection laws or regulations.
          <br /> <br />
          {"   "} iii. Ownership Rights. Provider represents, warrants, and
          covenants that it has all necessary right, title, and interest in and
          to the content it submits to GoodFriend, including any and all rights
          and interests in or to Provider Information and any copyright,
          trademark, other intellectual property, publicity, and privacy
          (including necessary consents, authorizations, and any other legal
          permissions).
          <br /> <br />
          iv. Reviews. If Provider provides any Reviews to GoodFriend, Provider
          represents and warrants that it has obtained all rights, titles,
          interests, consents, and authorizations necessary from the applicable
          third-party reviewer and in accordance with applicable laws and
          regulations, for GoodFriend to publish such Reviews through the
          Services; no remuneration or compensation has been or will be provided
          to the applicable customer for his or her review; and each such Review
          has been provided to Provider or its agent by a customer of Provider
          in connection with services actually provided or products actually
          sold by the Provider.
          <br /> <br />
          b. Mutual Warranties. Each party hereto represents and warrants that:
          (i) it is duly organized, validly existing, and in good standing under
          the laws of the jurisdiction in which it is organized; (ii) it has the
          full power to enter into this Agreement and to perform its obligations
          hereunder; (iii) the execution and delivery of this Agreement will not
          result in any breach of any terms and conditions of, or constitute a
          default under, any other agreement to which such party is bound; and
          (iv) it is not currently the subject of a voluntary or involuntary
          petition in bankruptcy, does not currently contemplate filing any such
          voluntary petition, and is not aware of any basis for the filing of an
          involuntary petition.
          <br /> <br />
          c. No Other Warranties. THE GOODFRIEND WEBSITE AND SERVICES ARE
          PROVIDED “AS IS” AND “AS AVAILABLE” WITHOUT WARRANTY OF ANY KIND.
          EXCEPT AS EXPRESSLY SET FORTH IN THESE TERMS. TO THE MAXIMUM EXTENT
          PERMITTED BY APPLICABLE LAW, GOODFRIEND MAKES NO WARRANTIES (INCLUDING
          THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
          PURPOSE, AND NON-INFRINGEMENT), GUARANTEES, REPRESENTATIONS, PROMISES,
          STATEMENTS, ESTIMATES, CONDITIONS, OR OTHER INDUCEMENTS, EXPRESS,
          IMPLIED, ORAL, WRITTEN, OR OTHERWISE, EXCEPT AS EXPRESSLY SET FORTH
          HEREIN. GOODFRIEND DOES NOT WARRANT OR GUARANTEE QUALITY; OFFER
          PERFORMANCE; CONVERSION RATES; RESPONSE RATES; OR THAT PROVIDER WILL
          RECEIVE A CERTAIN NUMBER OF CUSTOMERS OR AMOUNT OF PROFITS. GOODFRIEND
          HEREBY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, ORAL OR WRITTEN,
          INCLUDING WITHOUT LIMITATION ALL IMPLIED WARRANTIES OF TITLE,
          NON-INFRINGEMENT, ACCURACY, MERCHANTABILITY, OR FITNESS FOR ANY
          PARTICULAR PURPOSE, ALL WARRANTIES ARISING FROM ANY COURSE OF DEALING
          OR PERFORMANCE OR USAGE OF TRADE, AND ALL WARRANTIES OF THE SERVICES,
          SOFTWARE, OR THE GOODFRIEND WEBSITE BEING BUG-FREE, ERROR-FREE, OR
          FREE FROM DEFECTS.
        </Body>
        <IndentSubtitle>Indemnification.</IndentSubtitle>
        <Body>
          Provider will defend, indemnify, and hold harmless GoodFriend and its
          affiliates, directors, officers, employees, consultants, and agents
          from any and all claims, actions, proceedings, losses, damages,
          liabilities, and expenses, including reasonable attorneys’ fees and
          amounts awarded by a court or paid in settlement, arising from or
          related to: (i) any services provided by Provider or any of its
          employees, independent contractors, or agents to a Customer or other
          person resulting from the use of the Services or the GoodFriend
          Website, including the Provider Services; (ii) any fees, costs, or
          expenses in connection with any services or treatment provided by
          Provider to a Customer resulting from Provider’s use of the Services
          or the GoodFriend Website; (iii) use of the Services by, or provision
          of the Services to, any other Provider customers; (iv) gross
          negligence, willful or intentional misconduct, or fraud; (v) any
          breach of this Agreement or violation of any applicable law, rule, or
          regulation; (vi) any breach of a third party’s rights; (vii) the
          Provider Information; or (viii) Provider’s use of, storage, or
          disclosure of Customer Information.
          <IndentSubtitle>Limitation of Liability.</IndentSubtitle>
          <Block>
            a. Disclaimer. TO THE MAXIMUM EXTENT PERMITTED BY LAW, UNDER NO
            CIRCUMSTANCES SHALL GOODFRIEND OR ITS PARTNERS, SUPPLIERS, VENDORS,
            OR LICENSORS BE LIABLE TO PROVIDER OR PROVIDER’S EMPLOYEES,
            OFFICERS, AGENTS, OR INDEPENDENT CONTRACTORS FOR ANY (i) LOSS OF
            PROFITS, LOST REVENUE, INDIRECT, INCIDENTAL, SPECIAL, PUNITIVE,
            EXEMPLARY OR CONSEQUENTIAL DAMAGES, (ii) LOSS OF DATA, (iii) LOSS OR
            INTERRUPTION OF USE OF THE SERVICES, SOFTWARE, OR GOODFRIEND
            WEBSITE, (iv) PROVISION OF SERVICES TO CUSTOMERS OR PROVIDER’S
            NON-GOODFRIEND CUSTOMERS, (v) LOSS OF PROFITS OR SALES; OR (vi)
            REVIEWS, WHETHER IN AN ACTION IN CONTRACT, TORT, OR BASED ON A
            WARRANTY, EVEN IF GOODFRIEND HAS BEEN ADVISED OF THE POSSIBILITY OF
            SUCH DAMAGES AND LOSSES.
            <br /> <br />
            b. Provider Services. Provider acknowledges and agrees that
            GoodFriend does not render or fulfill any Provider Services, and
            Provider shall be exclusively responsible for fulfilling all
            Customer requests.
            <br /> <br />
            c. Limitation. TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT
            SHALL THE MAXIMUM AGGREGATE LIABILITY OF GOODFRIEND OR ITS PARTNERS,
            VENDORS, SUPPLIERS, OR LICENSORS FOR ANY LOSS OR DAMAGES (WHETHER
            BASED IN CONTRACT, TORT, OR ANY OTHER FORM OF ACTION) (A) RELATING
            TO OR ARISING OUT OF THIS AGREEMENT, THE GOODFRIEND WEBSITE, OR THE
            SERVICES; OR (B) RELATING TO OR ARISING OUT OF ANY OTHER AGREEMENT
            BETWEEN PROVIDER AND GOODFRIEND OR ANY THIRD PARTY THAT IS A
            PARTNER, VENDOR, SUPPLIER, LICENSOR, OR LICENSEE OF GOODFRIEND,
            EXCEED THE GREATER OF: (Y) THE AGGREGATE OF THE AMOUNT OF FEES
            ACTUALLY PAID BY PROVIDER TO GOODFRIEND HEREUNDER DURING THE
            PREVIOUS TWELVE (12)-MONTH PERIOD, OR (Z) ONE HUNDRED DOLLARS
            ($100).
          </Block>
        </Body>
        <IndentSubtitle>Marks; Non-Disparagement.</IndentSubtitle>
        <Body>
          Any use of a GoodFriend Mark by Provider that is not expressly
          permitted hereunder shall, in each case, be subject to the prior
          written approval of GoodFriend. GoodFriend reserves the right to
          terminate Provider’s right to use any GoodFriend Mark immediately upon
          the issuance of written notice if GoodFriend determines in its sole
          discretion that Provider is in violation of any of GoodFriend’s
          guidelines or that such use might result in any harm to GoodFriend.
          Provider acknowledges and agrees that it shall not (i) make any
          comparative references to GoodFriend, its pricing, the Services, or
          the GoodFriend Website; (ii) disparage or otherwise denigrate
          GoodFriend, the Services, or the GoodFriend Website; or (iii) promote
          a competitive offering. Provider grants GoodFriend a non-exclusive,
          royalty-free license to use Provider Logos in connection with the
          Services during the Term.
        </Body>
        <IndentSubtitle>Intellectual Property. </IndentSubtitle>
        <Body>
          All right, title, and interest, including without limitation all
          intellectual property rights, in and to the Services, Software, the
          GoodFriend Marks, and the GoodFriend Website, including all content
          submitted by Customers or Provider thereto (other than Provider
          Logos), as well as all URLs and domains registered by GoodFriend (even
          if such URLs and domains incorporate Provider Logos), shall remain the
          valuable and exclusive property of GoodFriend. GoodFriend retains all
          proprietary rights, title, and interest, including, without
          limitation, all patents, copyrights, trademarks, service marks, trade
          dress, rights to the look and feel of the GoodFriend Website,
          Services, and trade secrets in and to any inventions, data,
          information, know-how, logos, ideas, concepts, technology, software,
          and documentation related to or resulting from the use of the Services
          or the GoodFriend Website.
        </Body>
        <IndentSubtitle> Confidentiality</IndentSubtitle>
        <Body>
          a. Confidential Information. "Confidential Information” means all
          confidential and proprietary information of one party (“Disclosing
          Party”) disclosed to the other party (“Receiving Party”), including
          the terms of this Agreement (including pricing terms), either party’s
          marketing strategies and methodologies, business plans, technology and
          technical information, product designs, business processes, targeting
          information and processes, test results, statistics, reports, and
          compliance information, regardless of whether or not such information
          is designated as confidential. For the purposes of this Agreement,
          Customer Personal Information shall constitute GoodFriend’s
          Confidential Information. However, Confidential Information shall not
          include any information that: (i) is or becomes generally known to the
          public without breach of any obligation owed to the Disclosing Party;
          (ii) was known to the Receiving Party prior to its disclosure by the
          Disclosing Party without breach of any obligation owed to the
          Disclosing Party; or (iii) was independently developed by the
          Receiving Party without breach of any obligation owed to the
          Disclosing Party.
          <br /> <br />
          b. Non-Disclosure. The Receiving Party shall not disclose or use any
          Confidential Information of the Disclosing Party for any purpose other
          than to fulfill its obligations under this Agreement, except with the
          Disclosing Party’s prior written permission. Notwithstanding the
          foregoing, the Receiving Party may disclose such Confidential
          Information to those of its employees and contractors who need to know
          such information for purposes of performing the Receiving Party’s
          obligations under this Agreement, provided that the Receiving Party
          certifies that such employees and contractors have agreed, either as a
          condition of employment or in order to obtain such Confidential
          Information, to be bound by terms and conditions substantially similar
          to those in this Agreement. The Receiving Party shall use the same
          degree of care to protect the Confidential Information of the
          Disclosing Party as it uses to protect its own information of a
          confidential and proprietary nature, but in no event less than a
          reasonable degree of care.
          <br /> <br />
          c. Compelled Disclosure. If Receiving Party is compelled by law or
          court of competent authority to disclose Confidential Information of
          the Disclosing Party, it must first notify the Disclosing Party and
          provide it ample time to seek a protective order or other relief
          before producing Confidential Information. In the event such
          protection is not obtained, or the Disclosing Party waives compliance
          with this provision, the Receiving Party agrees that it will disclose
          only that portion of the Confidential Information that it is legally
          required to disclose.
          <br /> <br />
          d. Injunctive Relief. If the Receiving Party discloses or uses (or
          states an intent to disclose or use) any Confidential Information of
          the Disclosing Party in breach of the confidentiality obligations
          hereunder, the Disclosing Party shall have the right, in addition to
          any other remedies available to it, to seek injunctive relief to
          enjoin such acts, it being specifically acknowledged by the parties
          that any other available remedies are inadequate.
          <br /> <br />
        </Body>
        <IndentSubtitle>Term; Termination.</IndentSubtitle>
        <Body>
          a. Term. This Agreement will commence on the date of Provider’s
          acceptance of the terms and conditions herein, whether by creating an
          Account or electronically signing this Agreement, or by downloading,
          accessing, or otherwise using the Services or the GoodFriend Website,
          and shall continue in effect for a period of one (1) year (the
          “Initial Term”). This Agreement will automatically renew for
          successive periods of the same length as the Initial Term unless
          either party notifies the other party in writing at least thirty (30)
          days prior to the end of the Initial Term, or any renewal period, of
          its election not to renew this Agreement. The Initial Term and all
          renewal periods are collectively referred to herein as the “Term.”
          <br /> <br />
          b. Termination. Provider or GoodFriend may terminate this Agreement if
          the other party breaches a material term of this Agreement and fails
          to cure such breach within ten (10) days of receiving notice. Without
          limiting the foregoing, GoodFriend reserves the right to suspend or
          terminate this Agreement if it suspects or determines that Provider or
          any person under its reasonable control has (i) violated any
          applicable law, rule, or regulation or the rights of any third party;
          (ii) engaged in inappropriate or unprofessional conduct or fraud;
          (iii) failed to fulfill the requirements set forth hereunder or failed
          to fulfill Provider Services scheduled by Customers; or (iv) otherwise
          breached any provision of this Agreement. GoodFriend also reserves the
          right, at its sole discretion, to terminate this Agreement immediately
          for multiple rescheduling or cancelling of appointments by Provider.
          <br /> <br />
          c. Survival. All rights to validly accrued fees, payments, withholding
          of payments, causes of action, and any provisions, which by their
          terms or nature are intended to survive termination, shall survive any
          termination of this Agreement.
        </Body>
        <IndentSubtitle>Miscellaneous.</IndentSubtitle>
        <Body>
          a. Assignment. Neither party may assign this Agreement, or any of its
          rights and obligations hereunder, without the prior written consent of
          the other party, except that GoodFriend has the unrestricted right to
          assign this Agreement to an affiliate or in the event of a sale,
          merger, or acquisition of any portion of its business to which this
          Agreement relates. Any attempted transfer in violation of this section
          will be void and of no effect. This Agreement will be binding upon,
          and inure to the benefit of, the successors, representatives, and
          permitted assigns of the parties.
          <br /> <br />
          b. Notices. All notices or other communications required or permitted
          to be given or delivered under this Agreement shall be in writing
          (unless otherwise specifically provided herein) and shall be deemed
          given if sent (i) to GoodFriend by overnight guaranteed delivery
          service and addressed to 440 Park Avenue, Fl. 5, New York, NY 10022
          Attn: Legal or by email to legal@kinship.co, return receipt requested;
          or (ii) to Provider via email to the designated billing contact or
          address on file or via the Provider Account Page. Provider expressly
          consents to the provision of notices via email. Either party may
          update its preferred notice address at any time upon proper notice to
          the other party. Any such notice or other communication shall be
          deemed to be given as of the date it is delivered to the recipient or,
          if delivered on a non-business day, on the next business day.
          <br /> <br />
          c. Governing Law. This Agreement shall be governed by, and construed
          in accordance with, the laws of the State of Delaware without
          reference to its conflict of laws provisions.
          <br /> <br />
          d. Arbitration Agreement. Provider and GoodFriend acknowledge and
          agree that any dispute, claim, question, or disagreement arising out
          of or relating to the subject matter of this Agreement must be
          communicated in writing to the other party. The parties shall use
          their best efforts to reach resolution directly through good-faith
          negotiations, which shall be a precondition to either party initiating
          arbitration. If, after sixty (60) days, such negotiations do not
          resolve the dispute, it shall be finally settled by binding
          arbitration in New York, NY before the American Arbitration
          Association conducted under the rules of the American Arbitration
          Association. Judgment upon the award rendered by the arbitrator may be
          entered in any court with proper jurisdiction. PROVIDER AND GOODFRIEND
          WAIVE ANY RIGHTS TO GO TO COURT AND HAVE A TRIAL IN FRONT OF A JUDGE
          OR JURY. In any litigation between Provider and GoodFriend over
          whether to vacate or enforce an arbitration award, PROVIDER AND
          GOODFRIEND WAIVE ALL RIGHTS TO A JURY TRIAL and elect instead to have
          the dispute be resolved by a judge. Notwithstanding the foregoing, the
          following shall not be subject to arbitration and may be adjudicated
          only in the state and federal courts of New York: (i) any dispute,
          controversy, or claim relating to or contesting the validity of
          GoodFriend’s or one of GoodFriend’s family company’s intellectual
          property rights and proprietary rights, including without limitation,
          patents, trade]marks, service marks, copyrights, or trade secrets or
          (ii) an action by GoodFriend for temporary or preliminary injunctive
          relief, whether prohibitive or mandatory, or other provisional relief.
          FURTHER, ALL CLAIMS AND DISPUTES WITHIN THE SCOPE OF THIS ARBITRATION
          AGREEMENT MUST BE ARBITRATED OR LITIGATED ON AN INDIVIDUAL BASIS AND
          NOT ON A CLASS OR COLLECTIVE BASIS. CLAIMS OF MORE THAN ONE PROVIDER
          CANNOT BE ARBITRATED OR LITIGATED JOINTLY OR CONSOLIDATED WITH THOSE
          OF ANY OTHER PROVIDER. However, if the prohibition against class
          actions and other claims brought on behalf of third parties, or any
          other part of this section, is found to be unenforceable, then all of
          the other language in this Section 12.d shall be deemed unenforceable.
          To the extent that Provider or GoodFriend are permitted to bring a
          claim relating to this Agreement in state or federal court, then such
          claim shall only be brought in courts located in New York.
          <br /> <br />
          e. Modifications. No modification, amendment, or waiver of this
          Agreement or any of its provisions shall be binding upon GoodFriend
          unless made in writing and agreed to by GoodFriend. Provider expressly
          acknowledges and agrees that GoodFriend shall not be subject to any
          terms not set forth herein, including, but not limited to, any
          click-through agreements GoodFriend may be required to accept in
          connection with its use thereof. GoodFriend may amend the terms and
          conditions of this Agreement by posting a notice on Provider’s Account
          or otherwise reasonably notifying Provider at least thirty (30) days
          prior to such change taking effect, including by posting on the
          GoodFriend Website. If Provider does not agree to such changes,
          Provider may terminate this Agreement without penalty by providing
          GoodFriend with written notice of such election prior to such
          modification taking effect. Provider’s continued use of the Services
          or the GoodFriend Website following such period shall be deemed to be
          Provider’s acceptance of such change. A failure or delay of either
          party to: insist upon the performance of any terms, conditions,
          rights, or privileges of this Agreement, or exercise any rights or
          privileges conferred in this Agreement, shall not be construed as
          waiving any such terms, conditions, rights, or privileges and the same
          shall continue and remain in full force and effect.
          <br /> <br />
          f. Severability. Except as expressly set forth herein, in the event
          any one or more of the provisions of this Agreement shall for any
          reason be held to be invalid, illegal, or unenforceable, the remaining
          provisions of this Agreement shall be unimpaired, and the invalid,
          illegal, or unenforceable provision shall be replaced by a mutually
          acceptable provision, which, being valid, legal, and enforceable,
          comes closest to the intention of the parties underlying the invalid,
          illegal, or unenforceable provision.
          <br /> <br />
          g. Entire Agreement. This Agreement, any exhibits or addenda hereto,
          the policies referred to herein, and any insertion order or order
          form, constitute the entire agreement between the parties hereto and
          supersede all previous or contemporaneous agreements, promises, or
          representations, whether written or oral, between the parties with
          respect to the subject matter hereof.
          <br /> <br />
          h. Headings. The headings of the sections of this Agreement are for
          convenience only and do not form a part hereof or in any way limit,
          define, describe, modify, interpret, or construe the meaning, scope,
          or intent of this Agreement or any terms or conditions therein.
          <br /> <br />
          i. Independent Contractors. The relationship of the parties shall be
          that of independent contractors. Neither party will represent that it
          has any authority to assume or create any obligation, express or
          implied, on behalf of the other party, or to represent the other party
          as agent, employee, or in any other capacity, except as specifically
          provided herein.
          <br /> <br />
          j. Third-Party Beneficiaries. This Agreement is entered into solely
          between, and made for the sole benefit of, GoodFriend and Provider,
          and, subject to indemnification obligations and the limitations of
          liability herein, this Agreement will not be deemed to create any
          obligations, remedies, or liabilities of a party to any third parties
          (including, without limitation, any of Provider’s officers, employees,
          agents, and independent contractors) unless explicitly stated herein.
          Except as otherwise stated in this Agreement, no third party shall
          have the right to make any claim or assert any right under this
          Agreement, and no third party shall be deemed a beneficiary of this
          Agreement.
          <br /> <br />
          k. Remedies Cumulative. Unless expressly stated, no remedy afforded to
          a party under this Agreement shall preclude other remedies available
          under equity or law.
        </Body>
      </PageWrapper>
    </ConditionalNavWrapper>
  );
}
